/// <reference path="./react-table-config.d.ts" />
import { makeStyles, Theme } from '@material-ui/core/styles'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { Row } from 'react-table'

import TableLoadingSkeleton from './TableLoadingSkeleton'

const tableBodyStyles = makeStyles((theme: Theme) => ({
    rowContainer: {
        borderBottom: '1px solid #C9C9C9',
        '& .sticky-left': {
            margin: 'unset !important',
            display: 'flex !important',
            alignItems: 'center',
            boxShadow: '8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
        '& .sticky-right': {
            margin: 'unset !important',
            display: 'flex !important',
            alignItems: 'center',
            boxShadow: '-8px 0px 8px rgba(0, 0, 0, 0.10)',
        },
    },

    centerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
    },
    grayText: {
        ...theme.typography.h1,
        color: theme.palette.grey[700],
    },
    iconFamily: {
        marginLeft: '16px !important',
    },
}))

export interface TableBodyProps<TData extends Object> {
    cellCssOverride?: CSSProperties
    checkboxColumnWidth: number
    fetchDataLoading: boolean
    fetchNextDataLoading?: boolean
    fetchNextPage?: () => void
    hasNextPage: boolean
    hideLoadingSkeleton?: boolean
    pageSize: number
    prepareRow: (row: Row<TData>) => void
    rows: Row<TData>[]
    skeletonItemHeight?: number
    projectsLoading?: boolean
    tableBodyHeight?: string
    paddingTop?: number
    paddingBottom?: number
    rowVirtualizer?: any
}

const TableBody = <TData extends Object>({
    cellCssOverride,
    checkboxColumnWidth,
    fetchDataLoading,
    fetchNextDataLoading,
    fetchNextPage,
    hasNextPage,
    hideLoadingSkeleton,
    pageSize,
    prepareRow,
    rows,
    skeletonItemHeight = 50,
    paddingTop = 0,
    paddingBottom = 0,
    rowVirtualizer,
}: TableBodyProps<TData>) => {
    const classes = tableBodyStyles(cellCssOverride)
    let leftStickyColumnIndex = 0,
        rightStickyColumnIndex = 0

    const [isLoaderInView, setIsLoaderInView] = useState(false)

    const loadNext = useCallback((inView) => {
        setIsLoaderInView(inView)
    }, [])

    useEffect(() => {
        if (
            isLoaderInView &&
            !fetchDataLoading &&
            !fetchNextDataLoading &&
            hasNextPage
        ) {
            fetchNextPage?.()
        }
    }, [isLoaderInView, fetchDataLoading, fetchNextDataLoading, fetchNextPage])

    return (
        <div>
            <div className="tr">
                <div
                    className="td"
                    style={{
                        height: `${paddingTop}px`,
                    }}
                />
            </div>
            {rowVirtualizer?.virtualItems.map((virtualRow: any) => {
                const row = rows[virtualRow.index]
                prepareRow(row)

                const { key: rowKey, ...rowProps } = row.getRowProps()

                return (
                    <div
                        key={rowKey}
                        {...rowProps}
                        className={clsx(
                            classes.rowContainer,
                            row.isGrouped && 'grouped-row',
                            row.isExpanded && 'expanded',
                            'tr'
                        )}
                    >
                        {row.cells.map((cell, index) => {
                            if (cell.column.sticky === 'left') {
                                leftStickyColumnIndex = index
                            } else if (
                                cell.column.sticky === 'right' &&
                                !rightStickyColumnIndex
                            ) {
                                rightStickyColumnIndex = index
                            }

                            return (
                                <div
                                    {...cell.getCellProps({
                                        style: {
                                            wordBreak: 'unset',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            verticalAlign: 'middle',
                                            minHeight: 24,
                                            minWidth:
                                                cell.column.sticky === 'left'
                                                    ? cell.column
                                                          .stickyLeftMargin ??
                                                      checkboxColumnWidth
                                                    : (cell.column as any)
                                                          ?.style
                                                    ? (cell.column as any)
                                                          ?.style?.minWidth
                                                    : 'unset',
                                            left:
                                                cell.column.sticky === 'left'
                                                    ? cell.column
                                                          .stickyLeftMargin ??
                                                      checkboxColumnWidth
                                                    : 'unset',
                                            right:
                                                cell.column.sticky === 'right'
                                                    ? cell.column
                                                          .stickyRightMargin ??
                                                      0
                                                    : 'unset',
                                            position: !cell.column.sticky
                                                ? 'unset'
                                                : 'sticky',
                                            zIndex: !cell.column.sticky ? 0 : 1,
                                            backgroundColor: (cell.column as any)
                                                ?.style
                                                ? (cell.column as any)?.style
                                                      ?.backgroundColor
                                                : 'white',
                                            ...cellCssOverride,
                                        },
                                    })}
                                    className={clsx({
                                        'grouped-cell':
                                            cell.isGrouped || cell.isAggregated,
                                        'sticky-right':
                                            rightStickyColumnIndex === index &&
                                            cell.column.sticky === 'right',
                                        'sticky-left':
                                            leftStickyColumnIndex === index &&
                                            cell.column.sticky === 'left',
                                        td: true,
                                    })}
                                >
                                    {cell.isAggregated
                                        ? cell.render('Aggregated')
                                        : cell.isPlaceholder
                                        ? null
                                        : cell.render('Cell')}
                                </div>
                            )
                        })}
                    </div>
                )
            })}
            <div className="tr">
                <div
                    className="td"
                    style={{
                        height: `${paddingBottom}px`,
                    }}
                />
            </div>

            <InView as="div" onChange={loadNext}>
                <div
                    style={{
                        height: '1px',
                        width: '100vw',
                    }}
                ></div>
            </InView>

            {hasNextPage && (
                <TableLoadingSkeleton
                    height={skeletonItemHeight}
                    pageSize={3}
                />
            )}

            {(fetchDataLoading || fetchNextDataLoading) &&
                !rowVirtualizer?.virtualItems.length && (
                    <TableLoadingSkeleton
                        height={skeletonItemHeight}
                        pageSize={pageSize}
                    />
                )}
        </div>
    )
}

export default TableBody
